import {
  Grid, Checkbox, TableContainer, Table,
  TableBody, TableRow, TableCell,
  Box, Tooltip, useMediaQuery, useTheme,
  Typography,
  Chip,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { OrderStatus } from '../../../enums/OrderStatus';
import { applyRoundAndFormat, formatCaseFillPercent, formatPalletQty, formatPromotionReservationDuration } from '../../../utils';
import TextWithOverflowTooltip from '../../../components/shared/TextWithOverflowTooltip';
import { isOrderCategory, OrderCategoriesTranslationMap } from '../../../enums/OrderCategories';
import './OrderDetails.css';
import NoProductImage from '../../../assets/images/NoProductImage.png';
import { useTranslation } from 'react-i18next';
import { OrderDetails, OrderItemDetails } from '../../../app/services/api/orderManagementClient';
import { createRef, useContext } from 'react';
import { UserContext } from '../../../components/shared/useUser';
import { System } from '../../../enums/System';
import { useNavigate } from 'react-router-dom';
import { starbucksFranchises } from '../../../enums/FranchiseNames';
import { GroupedTableVirtuoso } from 'react-virtuoso';
import { QuantityCell, SaveStatus } from './QuantityCell';
import { EmptyTable } from '../../../components/shared/EmptyTable';
import { getRelevantOrderQuantity } from './orderQuantity';

enum PriceOverrideReason {
  PromoOffer = 1,
  AccountManagerDecision = 2
}

const PriceOverrideReasonText: Record<number, string> = {
  [PriceOverrideReason.PromoOffer]: 'Promo Offer',
  [PriceOverrideReason.AccountManagerDecision]: 'Account Manager Decision',
};

const isPromoOrderItem = (item: Pick<OrderItemDetails, 'promotionAllocationId'>) => item.promotionAllocationId != null;

interface tableProps {
  filteredItems: OrderItemDetails[],
  orderDetails: OrderDetails | null | undefined,
  selectedItemIds: number[],
  handleCheckboxChange: (itemId: number, isChecked: boolean) => void,
  canDeleteOrderItem: () => boolean,
  canChangeOrderQuantity: () => boolean,
  currencyCode: string | undefined,
  canEditOrder: () => boolean,
  handleEditClick: (item: OrderItemDetails) => void,
  deliveryChargeTotal: string,
  isQuantityError: (item: OrderItemDetails) => boolean,
  quantityError: (item: OrderItemDetails) => string | null,
  handleConfirmDeletion: (itemId: number, itemNumber: number) => Promise<void>,
  autosaveItem: (setSaveStatus?: (status: SaveStatus) => void) => Promise<void>,
  handleOpenDialogForDeletion: (itemId: number, itemNumber: number) => void,
  getQuantity: (webOrderItemId: number, fallback: number) => number;
  getPromoQuantity: (webOrderItemId: number, fallback: number) => number;
  setQuantityInRef: (itemId: number, value: number) => void;
  setPromoQuantityInRef: (webOrderItemId: number, value: number) => void;
  clearFilters: () => void;
  productSearch: string;
  handleBrowseCatalogueClick: () => void;
}

const OrderTable: React.FC<tableProps> = ({ filteredItems, orderDetails, selectedItemIds, handleCheckboxChange, canDeleteOrderItem, canChangeOrderQuantity, currencyCode, canEditOrder, handleEditClick, deliveryChargeTotal, isQuantityError, quantityError, handleConfirmDeletion, autosaveItem, handleOpenDialogForDeletion, getQuantity, getPromoQuantity, setQuantityInRef, setPromoQuantityInRef, clearFilters, productSearch, handleBrowseCatalogueClick }) => {
  const { t } = useTranslation('orderDetails');
  const { hasPermissionTo, selectedStore, selectedSystem } = useContext(UserContext);
  const showStockLevels = selectedSystem?.id !== System.OrderlyForDistributors;
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const navigate = useNavigate();

  const isStarBucksRetail = starbucksFranchises.includes(selectedStore?.franchiseName ?? '');
  const showMessageWhenNoCost = isStarBucksRetail && orderDetails?.status != 'Order Confirmed';
  const getEffectiveQuantity = (item: OrderItemDetails) => {
    const orderedQuantity = isPromoOrderItem(item)
      ? getPromoQuantity(item.id, item.quantity)
      : getQuantity(item.id, item.quantity);

    return getRelevantOrderQuantity(orderDetails?.status, item, orderedQuantity);
  };

  const totalCost = showMessageWhenNoCost
    ? 0
    : filteredItems.reduce((sum, item) => {
      return sum + item.price * getEffectiveQuantity(item);
    }, 0);

  const basketItems = orderDetails?.items ?? filteredItems;
  const totalProducts = new Set(basketItems.map(item => item.itemId)).size;
  const totalQuantity = basketItems.reduce((sum, item) => {
    return sum + getEffectiveQuantity(item);
  }, 0);

  // Trust the parent's resolved total exclusively. `containerCharge` may be a per-container value
  // DERIVED from the pricing engine's total (total / count at the time it was computed) rather than
  // a genuine flat rate — re-multiplying it here by the current computeContainerCount(containerData)
  // is unsafe if containerData re-renders with a different count than was used to derive it, producing
  // a total that doesn't match what the engine actually resolved.
  const parsedDeliveryChargeTotal = parseFloat(deliveryChargeTotal);
  const deliveryCharge = Number.isNaN(parsedDeliveryChargeTotal) ? 0 : parsedDeliveryChargeTotal;

  // Returns true only for non-empty, parseable date
  const hasValidPromoDate = (dateStr?: string | null): boolean => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    return !Number.isNaN(d.getTime()) && d.getFullYear() > 1;
  };

  const getPromoEndDate = (endDateUtc?: string | null) => {
    if (!hasValidPromoDate(endDateUtc)) {
      return null;
    }
    return new Date(endDateUtc!).toLocaleDateString();
  };



  // Build a map of promo line items (keyed by itemId) so we can merge them into the normal line row
  const promoLineMap = new Map<number, OrderItemDetails>();
  const normalItemIds = new Set<number>();
  for (const item of filteredItems) {
    if (isPromoOrderItem(item)) {
      promoLineMap.set(item.itemId, item);
    } else {
      normalItemIds.add(item.itemId);
    }
  }

  // Display items: exclude promo lines that have a corresponding normal line (they'll be shown inline)
  const displayItems = filteredItems.filter(item => {
    if (isPromoOrderItem(item) && normalItemIds.has(item.itemId)) {
      return false; // merged into the normal line row
    }
    return true;
  });

  const categoryMap: Record<string, true> = {};

  for (const item of displayItems) {
    categoryMap[item.category] = true;
  }

  const categories = Object.keys(categoryMap).sort((a, b) =>
    a.localeCompare(b),
  );

  const groupCounts = categories.map(category =>
    displayItems.filter(item => item.category === category).length,
  );

  if (filteredItems.length === 0) {
    return <EmptyTable
      text={t('items')}
      searchTerm={productSearch}
      onClear={clearFilters}
      onAdd={handleBrowseCatalogueClick}
      addItemText={t('browseCatalogue')}
      onAddIsCreating={false} 
      withPaginationShown={false} />;
  }

  return (
    <TableContainer
      component={'div'}
      sx={{
        overflowX: 'auto',
        maxWidth: '100%',
      }}
    >
      <GroupedTableVirtuoso
        style={{
          height: 'max(95vh, 400px)',
          transform: 'none', // <— prevents Virtuoso wrapper from creating a new stacking context
          border: '0px',
        }}
        overscan={20}
        groupCounts={groupCounts}
        components={{
          Table: (props) => (
            <Table
              {...props}
              size="small"
              sx={{
                tableLayout: 'auto',
                minWidth: 900,
                border: '0px',
              }}
              aria-label="order form table"
            />
          ),
          TableRow: TableRow,
        }}
        fixedHeaderContent={() => (
          <TableRow sx={{
            borderBottom: '10px', margin: 0, position: 'sticky',
            top: 0,
          }}>
            <TableCell
              sx={{
                width: '52%',
                minWidth: '52%',
                position: 'sticky',
                top: 0,
                zIndex: 3, // ensure it stays above content
                backgroundColor: theme.palette.common.white,
                borderBottom: '0px solid',
                borderColor: theme.palette.common.white,
                textAlign: 'center',
              }}>
              {t('itemDetails')}
            </TableCell>
            <TableCell sx={{
              width: '8%',
              position: 'sticky',
              top: 0,
              zIndex: 3, // ensure it stays above content
              backgroundColor: theme.palette.common.white,
              borderBottom: '0px solid',
              borderColor: theme.palette.common.white,
              textAlign: 'center',
            }}>
              <Tooltip title={t('palletQtyTooltip')}
                arrow>
                <span>{t('palletQty')}</span>
              </Tooltip>
            </TableCell>
            <TableCell sx={{
              width: '8%',
              position: 'sticky',
              top: 0,
              zIndex: 3, // ensure it stays above content
              backgroundColor: theme.palette.common.white,
              borderBottom: '0px solid',
              borderColor: theme.palette.common.white,
              textAlign: 'center',
            }}>
              <Tooltip title={t('casePercentTooltip')}
                arrow>
                <span>{t('casePercent')}</span>
              </Tooltip>
            </TableCell>
            <TableCell sx={{
              width: '8%',
              position: 'sticky',
              top: 0,
              zIndex: 3, // ensure it stays above content
              backgroundColor: theme.palette.common.white,
              borderBottom: '0px solid',
              borderColor: theme.palette.common.white,
              textAlign: 'center',
            }}>
              {t('caseSize')}
            </TableCell>
            <TableCell sx={{
              position: 'sticky',
              top: 0,
              zIndex: 3, // ensure it stays above content
              backgroundColor: theme.palette.common.white,
              borderBottom: '0px solid',
              borderColor: theme.palette.common.white,
              textAlign: 'center',
            }}>
              {t('price')}
            </TableCell>
            <TableCell sx={{
              width: '10%',
              position: 'sticky',
              top: 0,
              zIndex: 3, // ensure it stays above content
              backgroundColor: theme.palette.common.white,
              borderBottom: '0px solid',
              borderColor: theme.palette.common.white,
              textAlign: 'center',
            }}>
              {t('ordered')}
            </TableCell>
            <TableCell sx={{
              width: '9%',
              position: 'sticky',
              top: 0,
              zIndex: 3, // ensure it stays above content
              backgroundColor: theme.palette.common.white,
              borderBottom: '0px solid',
              borderColor: theme.palette.common.white,
              textAlign: 'center',
            }}>
              {t('confirmed')}
            </TableCell>
            <TableCell sx={{
              width: '9%',
              position: 'sticky',
              top: 0,
              zIndex: 3, // ensure it stays above content
              backgroundColor: theme.palette.common.white,
              borderBottom: '0px solid',
              borderColor: theme.palette.common.white,
              textAlign: 'center',
            }}>
              {t('shipped')}
            </TableCell>
            <TableCell sx={{
              width: '9%',
              position: 'sticky',
              top: 0,
              zIndex: 3, // ensure it stays above content
              backgroundColor: theme.palette.common.white,
              borderBottom: '0px solid',
              borderColor: theme.palette.common.white,
              textAlign: 'center',
            }}>
              {t('cost')}
            </TableCell>
            <TableCell sx={{
              width: '9%',
              position: 'sticky',
              top: 0,
              zIndex: 3, // ensure it stays above content
              backgroundColor: theme.palette.common.white,
              borderBottom: '0px solid',
              borderColor: theme.palette.common.white,
              textAlign: 'center',
            }}>
              {t('actions')}
            </TableCell>
          </TableRow>
        )}
        groupContent={(groupIndex) => {
          const category = categories[groupIndex];
          return (
            <TableCell
              key={`category-${category}`}
              colSpan={11}
              sx={{
                backgroundColor: theme.palette.primary.main,
                px: 3,
                py: 2,
                position: 'sticky',
                top: 38,
                zIndex: 1,
              }}
            >
              <Typography variant="textSM"
                color="white">
                {isOrderCategory(category)
                  ? t(
                    OrderCategoriesTranslationMap[
                      category as keyof typeof OrderCategoriesTranslationMap
                    ],
                  )
                  : category}
              </Typography>
            </TableCell>
          );
        }}
        itemContent={(itemIndex, groupIndex) => {
          const category = categories[groupIndex];
          const offset = groupCounts.slice(0, groupIndex).reduce((a, b) => a + b, 0);
          const item = displayItems.filter(x => x.category == category)?.[itemIndex - offset];
          const priceCellRef = createRef<HTMLSpanElement>();

          // When this item is itself a promo-only line (no corresponding normal line exists),
          // treat it as the promoLine so the promo chip / qty UI renders correctly.
          const isPromoOnlyLine = isPromoOrderItem(item)
            && !normalItemIds.has(item.itemId);
          const promoLine = isPromoOnlyLine
            ? item
            : !isPromoOrderItem(item)
              ? promoLineMap.get(item.itemId)
              : undefined;
          // For promo-only rows the "normal" quantity is 0 and the displayed item IS the promo line.
          const normalItem = isPromoOnlyLine ? null : item;
          const initialQuantity = normalItem ? getQuantity(normalItem.id, normalItem.quantity) : 0;
          const initialLinePrice = normalItem ? applyRoundAndFormat(normalItem.price * getEffectiveQuantity(normalItem), currencyCode) : '';
          const promoReservationDuration = formatPromotionReservationDuration(promoLine?.promotionReservationDurationMinutes);

          if (!item) {
            return null;
          }

          return (
            <>
              <TableCell sx={{
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '52%',
                minWidth: '52%', paddingLeft: 0,
              }}>
                <Grid sx={{
                  ...(((item.amendmentDetails || parseInt(item.priceOverrideReason ?? '') > 0) && orderDetails?.status === 'Amended')
                    ? { borderLeft: '4px solid #FFD600 !important' } // force yellow border
                    : {}),
                  display: 'flex', alignItems: 'center', paddingLeft: '8px',
                }}>
                  <Grid item
                    xs="auto">
                    <Checkbox
                      checked={selectedItemIds.includes(item.id)}
                      onChange={(event) => handleCheckboxChange(item.id, event.target.checked)}
                      disabled={!canDeleteOrderItem()}
                    />
                  </Grid>
                  <Grid item
                    sx={{ marginRight: '15px' }}
                    onClick={
                      hasPermissionTo(['ProductDetailView'])
                        ? () => navigate(`/ofd/products/${item.sku}`, {
                          state: {
                            orderId: orderDetails?.id,
                            fromOrderPage: true,
                            quantity: getQuantity(item.id, item.quantity),
                            canChangeOrderQuantity: canChangeOrderQuantity(),
                            orderItemId: item.id,
                            promoOrderItem: promoLine ?? null,
                          },
                        })
                        : undefined
                    }>
                    <Box
                      style={{
                        width: '100px',
                        height: isLargeScreen ? '75px' : '100px',
                        justifyContent: 'center',
                        backgroundColor: (item?.thumbnailUrl ?? '') != '' ? theme.palette.common.white : theme.palette.custom.gray[200],
                        borderRadius: '8px',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                        border: '1px solid #ddd',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: 'contain',
                        imageRendering: '-webkit-optimize-contrast',
                        backgroundImage: `url(${item?.thumbnailUrl && item?.thumbnailUrl.trim() !== '' ? item.thumbnailUrl : NoProductImage})`,
                      }}
                    />
                  </Grid>


                  <Grid item
                    sx={{ width: '65%' }}
                  >
                    <TextWithOverflowTooltip text={
                      item.name + (item.amendmentDetails && orderDetails?.status === 'Amended'
                        ? ` (${item.amendmentDetails})`
                        : '')
                    }
                    />
                    <Typography variant='textXS'
                      display="block">
                      {t('itemNumber')}: {item.sku}
                    </Typography>
                    <Typography variant='textXS'
                      display="block">
                      {t('orderableUOM')} {item.orderableUom} ({item.packSize}
                      {item.primaryUom})
                    </Typography>
                    <Typography variant='textXS'
                      display="block">
                      {t('primaryUOM')} {item.primaryUom}
                    </Typography>
                    <Box>
                      {showStockLevels && (
                        <>
                          <Tooltip title={`${t('stockOnHand')}: ${item.stockOnHand}`}
                            arrow>
                            <Chip
                              size="small"
                              color="primary"
                              variant='outlined'
                              label={`${t('stockOnHandABR')} ${item.stockOnHand}`}
                              sx={{ mr: 2 }}
                            />
                          </Tooltip>
                          <Tooltip title={`${t('stockOnOrder')}: ${item.stockOnOrder}`}
                            arrow>
                            <Chip
                              size="small"
                              color="warning"
                              variant='outlined'
                              label={`${t('StockOnOrderABR')} ${item.stockOnOrder}`}
                              sx={{ mr: 2 }}
                            />
                          </Tooltip>
                        </>
                      )}
                    </Box>
                    {/* Promotion indicator */}
                    {promoLine && (
                      <Box sx={{ mt: 0.5 }}>
                        <Tooltip
                          arrow
                          title={
                            <Box sx={{ p: 0.5 }}>
                              {promoLine.promotionName && (
                                <Typography sx={{ fontSize: '12px', fontWeight: 600, mb: 0.5 }}>{promoLine.promotionName}</Typography>
                              )}
                              <Typography sx={{ fontSize: '11px' }}>
                                {promoLine.promotionDiscountPercentage != null
                                  ? t('promoDiscountAppliedPerUnit', { discount: promoLine.promotionDiscountPercentage })
                                  : t('promoFixedPricePerUnit', { price: applyRoundAndFormat(promoLine.promoUnitPrice ?? promoLine.price, currencyCode) })}
                              </Typography>
                              {(promoLine.promotionQuantityCap != null || promoLine.promotionRemainingQuantity != null) && (
                                <Typography sx={{ fontSize: '11px', mt: 0.25 }}>
                                  {t('promoAvailable', {
                                    remaining: promoLine.promotionRemainingQuantity ?? '—',
                                    cap: promoLine.promotionQuantityCap ?? '—',
                                  })}
                                </Typography>
                              )}
                              {promoReservationDuration && (
                                <Typography sx={{ fontSize: '11px', mt: 0.25 }}>
                                  {t('promoReservationDuration', { duration: promoReservationDuration })}
                                </Typography>
                              )}
                            </Box>
                          }
                        >
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}>
                            <Chip
                              icon={<LocalOfferIcon sx={{ fontSize: 13 }} />}
                              size="small"
                              label={t('promoAllocation', { quantity: getPromoQuantity(promoLine.id, promoLine.quantity), unitPrice: applyRoundAndFormat(promoLine.price, currencyCode) })}
                              sx={{
                                bgcolor: '#E8F5E9',
                                color: '#2E7D32',
                                fontWeight: 600,
                                fontSize: '11px',
                                height: 22,
                                '& .MuiChip-icon': { color: '#2E7D32' },
                              }}
                            />
                            <InfoOutlinedIcon sx={{ fontSize: 13, color: '#2E7D32', flexShrink: 0 }} />
                          </Box>
                        </Tooltip>
                        {getPromoEndDate(promoLine.promotionEndDateUtc) && (
                          <Typography sx={{ fontSize: '11px', color: theme.palette.custom.gray[500], mt: 0.25 }}>
                            {t('promoEndDate', { date: getPromoEndDate(promoLine.promotionEndDateUtc) })}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </TableCell >

              <TableCell align="center"
                sx={{ width: '8%', verticalAlign: 'middle' }}>
                {formatPalletQty(item.casesPerPallet)}
              </TableCell>

              <TableCell align="center"
                sx={{ width: '8%', verticalAlign: 'middle' }}>
                <Tooltip title={t('casePercentTooltip')}
                  arrow>
                  <span>{formatCaseFillPercent(item.casesPerPallet)}</span>
                </Tooltip>
              </TableCell>

              <TableCell align="center"
                sx={{ width: '8%', verticalAlign: 'middle' }}>
                {item.caseSize != null ? item.caseSize : '—'}
              </TableCell>

              <TableCell sx={{ width: '8%' }}>
                {(showMessageWhenNoCost || (!item.priceOverrideReason && item.price === 0)) ?
                  <TextWithOverflowTooltip text='' />
                  :
                  (
                    <Box>
                      {!isPromoOnlyLine && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {t('pricePerUom', {
                            price: applyRoundAndFormat(item.price, currencyCode),
                            uom: item.orderableUom,
                          })}
                          {orderDetails?.status === OrderStatus.Review && hasPermissionTo(['PriceOverrideWrite']) &&
                              <IconButton
                                size="small"
                                disabled={!canEditOrder()}
                                onClick={() => handleEditClick(item)}
                                sx={{ ml: 1 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                          }
                          {item.gbpPrice !== undefined && item.price !== item.gbpPrice && (
                            <span
                              style={{
                                color: theme.palette.text.disabled,
                                fontStyle: 'italic',
                                marginLeft: 6,
                                fontSize: '0.95em',
                              }}
                            >
                                ({applyRoundAndFormat(item.gbpPrice, 'GBP')})
                            </span>
                          )}
                        </Box>
                      )}
                      {promoLine && (
                        <Box sx={isPromoOnlyLine ? undefined : { mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                          {promoLine.priceOverrideOriginalPrice != null && (
                            <Typography sx={{ fontSize: '11px', color: 'text.secondary', textDecoration: 'line-through', lineHeight: 1.2 }}>
                              {t('pricePerUom', {
                                price: applyRoundAndFormat(promoLine.priceOverrideOriginalPrice, currencyCode),
                                uom: promoLine.orderableUom,
                              })}
                            </Typography>
                          )}
                          <Typography sx={{ fontSize: '12px', color: '#2E7D32', fontWeight: 600 }}>
                            {t('pricePerUom', {
                              price: applyRoundAndFormat(promoLine.price, currencyCode),
                              uom: promoLine.orderableUom,
                            })}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
              </TableCell>
              <TableCell align="center"
                sx={{ verticalAlign: 'middle' }}>
                <Box>
                  {promoLine && !isPromoOnlyLine && (
                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                      {t('normalQty')}
                    </Typography>
                  )}
                  {!isPromoOnlyLine && (
                    <QuantityCell
                      item={item}
                      initialQuantity={initialQuantity}
                      canChange={canChangeOrderQuantity()}
                      isQuantityError={isQuantityError}
                      quantityError={quantityError}
                      setQuantityInRef={setQuantityInRef}
                      autosaveItem={autosaveItem}
                      handleConfirmDeletion={handleConfirmDeletion}
                    />
                  )}
                  {promoLine && (
                    <Box sx={isPromoOnlyLine ? { mt: 1 } : { mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                      <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#2E7D32', mb: 0.5 }}>
                        {t('promoQty')}
                      </Typography>
                      <QuantityCell
                        item={promoLine}
                        initialQuantity={getPromoQuantity(promoLine.id, promoLine.quantity)}
                        canChange={canChangeOrderQuantity()}
                        isQuantityError={isQuantityError}
                        quantityError={quantityError}
                        setQuantityInRef={setPromoQuantityInRef}
                        autosaveItem={autosaveItem}
                        handleConfirmDeletion={handleConfirmDeletion}
                        max={promoLine.quantity + (promoLine.promotionRemainingQuantity ?? 0)}
                      />
                    </Box>
                  )}
                </Box>
              </TableCell>
              <TableCell align="center"
                sx={{ verticalAlign: 'middle' }}>
                <Box>
                  {!isPromoOnlyLine && item.confirmedQuantity}
                  {promoLine && (
                    <Box sx={isPromoOnlyLine ? undefined : { mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                      {promoLine.confirmedQuantity}
                    </Box>
                  )}
                </Box>
              </TableCell>
              <TableCell align="center"
                sx={{ verticalAlign: 'middle' }}>
                <Box>
                  {!isPromoOnlyLine && item.shippedQuantity}
                  {promoLine && (
                    <Box sx={isPromoOnlyLine ? undefined : { mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                      {promoLine.shippedQuantity}
                    </Box>
                  )}
                </Box>
              </TableCell>
              <TableCell align="center"
                sx={{ verticalAlign: 'middle' }}>
                <Box>
                  {!isPromoOnlyLine && <span ref={priceCellRef}>{initialLinePrice}</span>}
                  {promoLine && (
                    <Box sx={isPromoOnlyLine ? undefined : { mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                      <Typography sx={{ fontSize: '12px', color: '#2E7D32', fontWeight: 500 }}>
                        {applyRoundAndFormat(promoLine.price * getEffectiveQuantity(promoLine), currencyCode)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </TableCell>
              <TableCell align="center"
                sx={{ verticalAlign: 'middle' }}>
                <Tooltip title={(isPromoOnlyLine ? promoLine!.id : item.id) === 0 ? t('cannotRemoveItem') : ''}>
                  <span>
                    <IconButton
                      color="error"
                      disabled={!canDeleteOrderItem() || (isPromoOnlyLine ? promoLine!.id : item.id) === 0}
                      onClick={() => handleOpenDialogForDeletion(isPromoOnlyLine ? promoLine!.id : item.id, isPromoOnlyLine ? promoLine!.itemId : item.itemId)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
            </>
          );
        }}
      />
      <Box mt={2}>
        <Table size="small">
          <TableBody>
            {filteredItems.length > 0 && (
              <>
                <TableRow>
                  <TableCell colSpan={9}
                    align="right"
                    sx={{ fontWeight: 'normal' }}>
                    {t('totalProducts')}
                  </TableCell>
                  <TableCell align="center"
                    sx={{ fontWeight: 'normal' }}>
                    {totalProducts}
                  </TableCell>
                  <TableCell />
                </TableRow>
                <TableRow>
                  <TableCell colSpan={9}
                    align="right"
                    sx={{ fontWeight: 'normal' }}>
                    {t('totalQuantity')}
                  </TableCell>
                  <TableCell align="center"
                    sx={{ fontWeight: 'normal' }}>
                    {totalQuantity}
                  </TableCell>
                  <TableCell />
                </TableRow>
                <TableRow>
                  <TableCell colSpan={9}
                    align="right"
                    sx={{ fontWeight: 'normal' }}>
                    {t('subtotal')}
                  </TableCell>
                  <TableCell align="center"
                    sx={{ fontWeight: 'normal' }}>
                    {applyRoundAndFormat(totalCost, currencyCode)}
                  </TableCell>
                  <TableCell />
                </TableRow>
                <TableRow>
                  <TableCell colSpan={9}
                    align="right"
                    sx={{ fontWeight: 'normal' }}>
                    {t('deliveryCharge')}
                  </TableCell>
                  <TableCell align="center"
                    sx={{ fontWeight: 'normal' }}>
                    {applyRoundAndFormat(deliveryCharge, currencyCode)}
                  </TableCell>
                  <TableCell />
                </TableRow>
                <TableRow sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                  <TableCell colSpan={9}
                    align="right"
                    sx={{ fontWeight: 'bold' }}>
                    {t('totalCost')}
                  </TableCell>
                  <TableCell align="center"
                    sx={{ fontWeight: 'bold' }}>
                    {applyRoundAndFormat(totalCost + deliveryCharge, currencyCode)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </Box>
    </TableContainer >
  );
};

export default OrderTable;
